import { useEffect, useMemo, useRef } from 'preact/hooks';
import {
  DEFAULT_MAP_POOL,
  MAX_MAP_POOL,
  MAX_PLAYERS,
  PERK_SLOTS,
  RANDOM_MAP,
  WEALTHS,
  WEALTH_NAMES,
  getUnlock,
  isPvpMode,
  raceOf,
  type GameMode,
  type LobbyPlayer,
  type MatchSettings,
} from '@cratered/shared';
import { levelById } from '../game/campaign';
import { lookFor } from '../game/look';
import { FAMILIES, familyName } from '../game/mapgen';
import { MAP_CATEGORIES, PLAYABLE_MAP_IDS, mapBlurb, mapName, poolFor, type MapCategory } from '../game/maps';
import { TEAM_COLORS } from '../game/render';
import { discord, isHost, lobby, me, net, profile, screen, self, showToast, speaking } from '../store';
import { Avatar, MoleCanvas, TeamDot } from './common';

const MODE_BLURB: Record<GameMode, { title: string; text: string; fair: string }> = {
  versus: {
    title: 'Versus',
    text: 'Two to eight players, teams or free-for-all. Grenades, guns and the boot. Last team on dry land wins.',
    fair: 'Cosmetics only. No perks.',
  },
  brawl: {
    title: 'Brawl',
    text: 'Versus with no turns: everyone moves, aims and fires at once, one mole each, cooldowns instead of turns. Last team standing wins the round.',
    fair: 'Cosmetics only. No perks.',
  },
  coop: {
    title: 'Co-op Siege',
    text: 'Up to four moles hold the map against waves of raiders. Each wave is bigger and tougher.',
    fair: 'Locker perks apply.',
  },
  campaign: {
    title: 'Solo Campaign',
    text: 'Thirty hand-tuned levels. Earn up to three stars each; stars unlock perks.',
    fair: 'Locker perks apply.',
  },
};

/** Dev only: `?devmap=<id>[&devmode=coop]` starts a match on that map as soon as the lobby opens. */
let devStarted = false;

export function Lobby() {
  const l = lobby.value;
  const p = profile.value;
  if (!l || !p || !self.value) return <div class="loading"><p class="muted">Joining the lobby…</p></div>;
  const host = isHost.value;
  const settings = l.settings;
  const players = l.players.filter((x) => !x.spectator);
  const spectators = l.players.filter((x) => x.spectator);
  const mine = me.value;
  const readyCount = players.filter((x) => x.ready && x.connected).length;
  const hostPlayer = l.players.find((x) => x.id === l.hostId);
  const level = levelById(settings.campaignLevelId);

  const update = (patch: Partial<MatchSettings>) => net.value?.send({ t: 'updateSettings', settings: patch });
  const setMode = (mode: GameMode) => {
    const patch: Partial<MatchSettings> = { mode };
    if (mode === 'campaign' && !settings.campaignLevelId) {
      screen.value = 'campaign';
    }
    update(patch);
  };

  // Both PvP modes need someone to fight; the PvE modes can start solo.
  const canStart = host && (!isPvpMode(settings.mode) || players.filter((x) => x.connected).length >= 2) && !(settings.mode === 'campaign' && !level);

  const look = useMemo(
    () => lookFor(p.equipped, TEAM_COLORS[(mine?.team ?? 0) % TEAM_COLORS.length]),
    [p.equipped.race, p.equipped.body, p.equipped.outfit, p.equipped.custom?.body, p.equipped.custom?.head, mine?.team],
  );

  useEffect(() => {
    if (!import.meta.env.DEV || devStarted || !host) return;
    const q = new URLSearchParams(window.location.search);
    const map = q.get('devmap');
    if (!map) return;
    devStarted = true;
    net.value?.send({ t: 'updateSettings', settings: { map, mode: (q.get('devmode') as GameMode | null) ?? 'coop', turnSeconds: 60 } });
    setTimeout(() => net.value?.send({ t: 'startMatch' }), 500);
  }, [host]);

  return (
    <div class="screen">
      <div class="lobby">
        {/* Players */}
        <section class="panel" aria-label="Players">
          <div class="panel-head">
            <h2>Players</h2>
            <span class="muted num">
              {players.length}/{MAX_PLAYERS[settings.mode]}
            </span>
          </div>
          <div class="panel-body">
            {players.map((pl) => (
              <PlayerRow key={pl.id} p={pl} hostId={l.hostId} mode={settings.mode} teams={settings.teams} />
            ))}
            {players.length < 2 && (
              <div class="invite-cta">
                <p>{isPvpMode(settings.mode) ? `${MODE_BLURB[settings.mode].title} needs at least two moles.` : 'More moles, more fun.'}</p>
                <button class="btn primary" onClick={() => discord.value?.openInviteDialog()}>
                  Invite friends
                </button>
              </div>
            )}
            {spectators.length > 0 && (
              <>
                <p class="eyebrow" style={{ marginTop: 6 }}>Spectating · {spectators.length}</p>
                {spectators.map((pl) => (
                  <PlayerRow key={pl.id} p={pl} hostId={l.hostId} mode={settings.mode} teams={settings.teams} />
                ))}
              </>
            )}
            {mine && (
              <button
                class="btn ghost"
                onClick={() => net.value?.send({ t: 'setSpectator', spectator: !mine.spectator })}
                disabled={!mine.spectator && settings.mode === 'campaign' && host === false}
              >
                {mine.spectator ? 'Join as a player' : 'Switch to spectating'}
              </button>
            )}
          </div>
        </section>

        {/* Preview + mode */}
        <section class="panel" aria-label="Game mode">
          <div class="panel-head">
            <div class="mode-tabs" role="tablist" style={{ flex: 1 }}>
              {(['versus', 'brawl', 'coop', 'campaign'] as GameMode[]).map((m) => (
                <button
                  key={m}
                  role="tab"
                  class="mode-tab"
                  aria-selected={settings.mode === m}
                  disabled={!host}
                  onClick={() => setMode(m)}
                >
                  {MODE_BLURB[m].title}
                </button>
              ))}
            </div>
          </div>
          <div class="panel-body" style={{ flex: 1 }}>
            <div class="preview-stage">
              <MoleCanvas look={look} aim big />
              <div class="preview-caption">
                <span class="tag">{getUnlock(raceOf(p.equipped))?.name}</span>
                <span class="tag">{getUnlock(p.equipped.body)?.name}</span>
                <span class="tag">{getUnlock(p.equipped.outfit)?.name}</span>
                <span class="tag">{getUnlock(p.equipped.trail)?.name}</span>
                {!isPvpMode(settings.mode) && p.perks.length > 0 && (
                  <span class="tag pve">{p.perks.length}/{PERK_SLOTS} perks</span>
                )}
              </div>
            </div>
            <div class="mode-blurb">
              <h3>{MODE_BLURB[settings.mode].title}</h3>
              <p class="muted">{MODE_BLURB[settings.mode].text}</p>
              <p class="eyebrow">{MODE_BLURB[settings.mode].fair}</p>
            </div>
            {settings.mode === 'campaign' && (
              <div class="setting" style={{ borderBottom: 0 }}>
                <label>
                  {level ? `Level ${level.index} — ${level.name}` : 'No level chosen'}
                  <span class="hint">{level ? level.blurb : 'Pick one from the campaign map.'}</span>
                </label>
                <button class="btn" onClick={() => (screen.value = 'campaign')}>
                  {level ? 'Change' : 'Choose level'}
                </button>
              </div>
            )}
            <div class="btn-row">
              <button class="btn" onClick={() => (screen.value = 'locker')}>
                Customize in Locker
              </button>
            </div>
          </div>
        </section>

        {/* Settings */}
        <section class="panel" aria-label="Match settings">
          <div class="panel-head">
            <h2>Settings</h2>
            {!host && hostPlayer && <span class="readonly-note">{hostPlayer.name} is host</span>}
          </div>
          <div class="panel-body" style={{ flex: 1 }}>
            {/* The campaign's map comes with its level (chosen on the campaign screen), so only the other modes get the picker. */}
            {settings.mode !== 'campaign' && <MapPicker settings={settings} host={host} update={update} />}
            {/* Brawl has no turns and one mole per player, so it skips the turn clock and the squad size. */}
            {settings.mode !== 'campaign' && settings.mode !== 'brawl' && (
              <Setting label="Turn time" hint="Seconds per turn">
                <Seg
                  value={settings.turnSeconds}
                  options={[15, 30, 45, 60].map((v) => ({ v: v as 15 | 30 | 45 | 60, label: String(v) }))}
                  disabled={!host}
                  onChange={(turnSeconds) => update({ turnSeconds })}
                />
              </Setting>
            )}
            {settings.mode === 'versus' && (
              <Setting label="Moles per team">
                <Seg
                  value={settings.unitsPerTeam}
                  options={[1, 2, 3, 4].map((v) => ({ v: v as 1 | 2 | 3 | 4, label: String(v) }))}
                  disabled={!host}
                  onChange={(unitsPerTeam) => update({ unitsPerTeam })}
                />
              </Setting>
            )}
            {isPvpMode(settings.mode) && (
              <Setting label="Teams">
                <Seg
                  value={settings.teams}
                  options={[2, 3, 4, 5, 6, 7, 8].map((v) => ({ v, label: String(v) }))}
                  disabled={!host}
                  onChange={(teams) => update({ teams })}
                />
              </Setting>
            )}
            {settings.mode !== 'campaign' && (
              <>
                <Setting label="Rounds" hint={settings.mode === 'coop' ? 'Best of; every round is a fresh set of waves, with a buy phase first' : 'Best of; buy phase before each'} stack>
                  <Seg
                    value={settings.bestOf}
                    options={[1, 3, 5].map((v) => ({ v: v as 1 | 3 | 5, label: `Bo${v}` }))}
                    disabled={!host}
                    onChange={(bestOf) => update({ bestOf })}
                  />
                </Setting>
                <Setting label="Money" hint={settings.mode === 'coop' ? 'Coins for the crew. Upper class: 3x. Broke: half. Prices stay put' : 'Upper class: 3x the coins. Broke: half. Prices stay put'} stack>
                  <Seg
                    value={settings.wealth ?? 'normal'}
                    options={WEALTHS.map((v) => ({ v, label: WEALTH_NAMES[v] }))}
                    disabled={!host}
                    onChange={(wealth) => update({ wealth })}
                  />
                </Setting>
                <Setting label="Wind" hint="Pushes balls and grenades">
                  <Seg
                    value={settings.wind ? 'on' : 'off'}
                    options={[{ v: 'off', label: 'Off' }, { v: 'on', label: 'On' }]}
                    disabled={!host}
                    onChange={(v) => update({ wind: v === 'on' })}
                  />
                </Setting>
              </>
            )}
            {settings.mode === 'coop' && (
              <Setting label="Waves">
                <Seg
                  value={settings.waves}
                  options={[2, 3, 5].map((v) => ({ v, label: String(v) }))}
                  disabled={!host}
                  onChange={(waves) => update({ waves })}
                />
              </Setting>
            )}
            {settings.mode === 'campaign' && level && (
              <>
                <Setting label="Map">
                  <span class="muted">{mapName(level.map)}</span>
                </Setting>
                <Setting label="Your moles">
                  <span class="muted num">{level.playerUnits}</span>
                </Setting>
                <Setting label="Raiders">
                  <span class="muted num">{level.enemies.count} × {level.enemies.hp} HP</span>
                </Setting>
                <Setting label="Par" hint="Win within this many rounds for 2 stars">
                  <span class="muted num">{level.parRounds} rounds</span>
                </Setting>
              </>
            )}
            <Setting label="Quick win par" hint="Rounds to earn the Quick bonus">
              <Seg
                value={settings.parRounds}
                options={[4, 6, 8].map((v) => ({ v, label: String(v) }))}
                disabled={!host || settings.mode === 'campaign'}
                onChange={(parRounds) => update({ parRounds })}
              />
            </Setting>

            <div class="lobby-footer">
              {host ? (
                <>
                  <span class="muted num">
                    {readyCount}/{players.length} ready
                  </span>
                  <button
                    class="btn primary big"
                    disabled={!canStart}
                    onClick={() => {
                      if (!canStart) return;
                      net.value?.send({ t: 'startMatch' });
                    }}
                  >
                    Start match
                  </button>
                </>
              ) : mine && !mine.spectator ? (
                <button
                  class={`btn big ${mine.ready ? '' : 'primary'}`}
                  onClick={() => net.value?.send({ t: 'setReady', ready: !mine.ready })}
                >
                  {mine.ready ? 'Ready ✓' : 'Ready up'}
                </button>
              ) : (
                <span class="muted">Waiting for {hostPlayer?.name ?? 'the host'} to start…</span>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

/**
 * The picker's quick filters: a chip adds every map wearing its tag to the pool, or drops them all
 * when they are already in. The families are whichever ones the catalogue actually holds.
 */
const TAG_CHIPS: readonly { tag: string; label: string }[] = [
  { tag: 'water', label: 'Water' },
  { tag: 'land', label: 'Land' },
  { tag: 'catapults', label: 'Catapults' },
  ...FAMILIES.filter((f) => poolFor(['generated'], [f]).length > 0).map((f) => ({ tag: f, label: familyName(f) })),
];
const CATEGORY_IDS: readonly MapCategory['id'][] = MAP_CATEGORIES.map((c) => c.id);

/**
 * The map setting. Random keeps a pool the server draws from at match start; category boxes, tag
 * chips and single maps all add to or drop from that one list, so the pool itself is the only
 * state and everyone in the lobby sees exactly what the host ticked. Pick one names a map outright.
 */
function MapPicker({ settings, host, update }: { settings: MatchSettings; host: boolean; update: (patch: Partial<MatchSettings>) => void }) {
  const random = settings.map === RANDOM_MAP;
  // No pool at all means the server's default; an empty one is the host's doing and is shown as such.
  const pool = useMemo(() => new Set(settings.mapPool ?? DEFAULT_MAP_POOL), [settings.mapPool]);
  // The last map picked outright, so flipping back from Random lands where the host left off.
  const lastPick = useRef<string | null>(null);
  if (!random) lastPick.current = settings.map;

  /** Sends the pool in picker order, deduped and capped, so every client lists it the same way. */
  const setPool = (ids: Set<string>) => update({ mapPool: PLAYABLE_MAP_IDS.filter((id) => ids.has(id)).slice(0, MAX_MAP_POOL) });
  const toggle = (ids: readonly string[], on: boolean) => {
    const next = new Set(pool);
    for (const id of ids) {
      if (on) next.add(id);
      else next.delete(id);
    }
    setPool(next);
  };
  const inPool = (ids: readonly string[]) => ids.filter((id) => pool.has(id)).length;
  const catIds = (c: MapCategory) => c.maps.map((m) => m.id);

  return (
    <div class="map-picker">
      <div class="setting" style={{ borderBottom: 0, paddingBottom: 0 }}>
        <label>
          Map
          <span class="hint">{random ? 'The server draws one from the pool at match start' : 'Everyone plays this one'}</span>
        </label>
        <Seg
          value={random ? 'random' : 'one'}
          options={[{ v: 'random', label: 'Random' }, { v: 'one', label: 'Pick one' }]}
          disabled={!host}
          onChange={(v) => update({ map: v === 'random' ? RANDOM_MAP : lastPick.current ?? [...pool][0] ?? PLAYABLE_MAP_IDS[0]! })}
        />
      </div>
      {random ? (
        <div class="map-pool">
          <div class="map-pool-head">
            <span class="num">
              <b>pool: {pool.size} maps</b>
              {pool.size === 0 && <span class="hint">Empty, so the server falls back to every arena and war map</span>}
            </span>
            <span class="btn-row">
              <button class="btn small" disabled={!host} onClick={() => setPool(new Set(PLAYABLE_MAP_IDS))}>All</button>
              <button class="btn small" disabled={!host} onClick={() => setPool(new Set())}>None</button>
            </span>
          </div>
          <div class="map-cats" role="group" aria-label="Map categories">
            {MAP_CATEGORIES.map((c) => {
              const n = inPool(catIds(c));
              const all = n === c.maps.length;
              return (
                <label key={c.id} class="check" title={c.blurb}>
                  <input type="checkbox" checked={all} indeterminate={n > 0 && !all} disabled={!host} onChange={() => toggle(poolFor([c.id]), !all)} />
                  <span>{c.label}</span>
                  <span class="count num">{n}/{c.maps.length}</span>
                </label>
              );
            })}
          </div>
          <div class="map-tags" role="group" aria-label="Map tags">
            {TAG_CHIPS.map((t) => {
              const ids = poolFor(CATEGORY_IDS, [t.tag]);
              const on = ids.length > 0 && inPool(ids) === ids.length;
              return (
                <button key={t.tag} class="tag-chip" aria-pressed={on} disabled={!host} title={`${ids.length} maps`} onClick={() => toggle(ids, !on)}>
                  {t.label}
                </button>
              );
            })}
          </div>
          <details class="map-list">
            <summary>Choose maps one by one</summary>
            {MAP_CATEGORIES.map((c) => (
              <details key={c.id} class="map-list-cat">
                <summary>
                  {c.label} <span class="count num">{inPool(catIds(c))}/{c.maps.length}</span>
                </summary>
                <div class="map-list-maps">
                  {c.maps.map((m) => (
                    <label key={m.id} class="check">
                      <input type="checkbox" checked={pool.has(m.id)} disabled={!host} onChange={() => toggle([m.id], !pool.has(m.id))} />
                      <span>{m.name}</span>
                    </label>
                  ))}
                </div>
              </details>
            ))}
          </details>
        </div>
      ) : (
        <div class="map-one">
          <select value={settings.map} disabled={!host} aria-label="Map" onChange={(e) => update({ map: (e.currentTarget as HTMLSelectElement).value })}>
            {!PLAYABLE_MAP_IDS.includes(settings.map) && <option value={settings.map}>{mapName(settings.map)}</option>}
            {MAP_CATEGORIES.map((c) => (
              <optgroup key={c.id} label={c.label}>
                {c.maps.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <p class="hint">
            <b>{mapName(settings.map)}.</b> {mapBlurb(settings.map)}
          </p>
        </div>
      )}
    </div>
  );
}

function PlayerRow({ p, hostId, mode, teams }: { p: LobbyPlayer; hostId: string; mode: GameMode; teams: number }) {
  const isMe = p.id === self.value?.id;
  const cycleTeam = () => {
    if (!isMe || !isPvpMode(mode)) return;
    net.value?.send({ t: 'setTeam', team: (p.team + 1) % teams });
  };
  return (
    <div class={`player-row${isMe ? ' me' : ''}${p.connected ? '' : ' offline'}`}>
      <Avatar name={p.name} url={p.avatarUrl} speaking={speaking.value.has(p.id)} large />
      <div style={{ minWidth: 0 }}>
        <div class="name">
          {p.name} {isMe && <span class="muted">(you)</span>}
        </div>
        <div class="sub">
          <span class="num">Lv {p.level}</span>
          {p.id === hostId && <span class="host-tag">host</span>}
          {!p.connected && <span>reconnecting…</span>}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {isPvpMode(mode) && !p.spectator && (
          <button
            class="btn icon ghost"
            onClick={cycleTeam}
            disabled={!isMe}
            title={isMe ? 'Switch team' : `Team ${p.team + 1}`}
            aria-label={isMe ? 'Switch team' : `Team ${p.team + 1}`}
          >
            <TeamDot team={p.team} />
          </button>
        )}
        {!p.spectator && p.id !== hostId && <span class={`ready-pill${p.ready ? ' on' : ''}`}>{p.ready ? 'Ready' : 'Not ready'}</span>}
      </div>
    </div>
  );
}

/** A settings row: label and hint on the left, the control on the right. `stack` puts the control under the label, for a wide control with a long hint. */
function Setting({ label, hint, stack, children }: { label: string; hint?: string; stack?: boolean; children: preact.ComponentChildren }) {
  return (
    <div class={`setting${stack ? ' stack' : ''}`}>
      <label>
        {label}
        {hint && <span class="hint">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

function Seg<T extends string | number>({
  value,
  options,
  onChange,
  disabled,
}: {
  value: T;
  options: { v: T; label: string }[];
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <div class="seg" role="group">
      {options.map((o) => (
        <button
          key={String(o.v)}
          aria-pressed={o.v === value}
          disabled={disabled}
          onClick={() => {
            if (disabled) {
              showToast('Only the host can change settings.');
              return;
            }
            onChange(o.v);
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
